import { capFirst } from '../lib/capfirst';
import { pipe } from 'effect/Function';

const TAG_PREFIX = 'tag:';
const CUSTOM_AGGREGATE_PREFIX = 'custom_aggregate:';
const CONVERSATION_CUSTOM_AGGREGATE_PREFIX = 'conversation_custom_aggregate:';

// Frontend filter/column kinds don't match the aggregate alias keys 1:1.
const AGGREGATE_ALIAS_KEY_BY_KIND: Record<string, string[]> = {
  topic: ['topic'],
  topics_conversations: ['topic'],
  conversation_topic: ['topic'],
  user_intent: ['intent'],
  type_of_problem_v2: ['model_suggestion'],
  model_suggestion: ['model_suggestion'],
  negative_user_intent_v2: ['moderation_classes', 'business_classes'],
  category: ['sub_topic'],
  conversation_category: ['sub_topic'],
  user_action: ['action'],
  conversation_user_action: ['action'],
  action: ['action'],
};

const FILTER_KIND_AGGREGATE_KEY: Record<string, string> = {
  user_action: 'action',
  conversation_user_action: 'action',
  category: 'sub_topic',
  conversation_category: 'sub_topic',
};

function isFactoryDefaultAggregateDisplayName(aggregateKey: string, displayName: string): boolean {
  if (aggregateKey === 'action') return displayName === 'Action';
  if (aggregateKey === 'sub_topic') return displayName.toLowerCase() === 'sub topic';
  return false;
}

function shouldDeferToFilterTranslation(kind: string, rawAlias: string): boolean {
  const aggregateKey = FILTER_KIND_AGGREGATE_KEY[kind];
  if (aggregateKey === undefined) return false;
  return isFactoryDefaultAggregateDisplayName(aggregateKey, rawAlias);
}

const clean = (name: string) => pipe(name, (s) => s.replace(/_/g, ' '), capFirst);

function shouldPrefixConversationLabel(kind: string): boolean {
  return kind.startsWith('conversation_') && !kind.startsWith(CONVERSATION_CUSTOM_AGGREGATE_PREFIX);
}

function applyConversationLabelPrefix(kind: string, label: string): string {
  if (kind.startsWith(CONVERSATION_CUSTOM_AGGREGATE_PREFIX) || shouldPrefixConversationLabel(kind)) {
    return `Conversation ${label}`;
  }
  return label;
}

export function resolveKindAlias(aliases: Record<string, Record<string, string>>, kind: string): string | undefined {
  if (kind.startsWith(TAG_PREFIX)) {
    return aliases.tag?.[kind.slice(TAG_PREFIX.length)];
  }
  if (kind.startsWith(CUSTOM_AGGREGATE_PREFIX)) {
    return aliases.aggregate?.[kind.slice(CUSTOM_AGGREGATE_PREFIX.length)];
  }
  if (kind.startsWith(CONVERSATION_CUSTOM_AGGREGATE_PREFIX)) {
    return aliases.aggregate?.[kind.slice(CONVERSATION_CUSTOM_AGGREGATE_PREFIX.length)];
  }
  const aggregateKeys = AGGREGATE_ALIAS_KEY_BY_KIND[kind];
  if (aggregateKeys) {
    for (const key of aggregateKeys) {
      const alias = aliases.aggregate?.[key];
      if (alias) return alias;
    }
  }
  return undefined;
}

type AggregateDisplayNameSource = {
  category_name: string;
  display_name: string;
  children?: AggregateDisplayNameSource[];
};

export function collectAggregateDisplayNames(
  aggregates: AggregateDisplayNameSource[],
): Record<string, string> {
  const result: Record<string, string> = {};

  const walk = (items: AggregateDisplayNameSource[]) => {
    for (const aggregate of items) {
      result[aggregate.category_name] = aggregate.display_name;
      if (aggregate.children?.length) walk(aggregate.children);
    }
  };

  walk(aggregates);
  return result;
}

function resolveAggregateDisplayName(
  kind: string,
  aggregateDisplayNames?: Record<string, string>,
): string | undefined {
  if (!aggregateDisplayNames) return undefined;

  if (kind.startsWith(CUSTOM_AGGREGATE_PREFIX)) {
    return aggregateDisplayNames[kind.slice(CUSTOM_AGGREGATE_PREFIX.length)];
  }
  if (kind.startsWith(CONVERSATION_CUSTOM_AGGREGATE_PREFIX)) {
    return aggregateDisplayNames[kind.slice(CONVERSATION_CUSTOM_AGGREGATE_PREFIX.length)];
  }

  return aggregateDisplayNames[kind];
}

export function formatKindAliasName(
  aliases: Record<string, Record<string, string>>,
  kind: string,
  aggregateDisplayNames?: Record<string, string>,
): string | undefined {
  const alias = resolveKindAlias(aliases, kind);
  if (alias !== undefined && !shouldDeferToFilterTranslation(kind, alias)) {
    return applyConversationLabelPrefix(kind, clean(alias));
  }

  const displayName = resolveAggregateDisplayName(kind, aggregateDisplayNames);
  if (displayName === undefined) return undefined;

  return applyConversationLabelPrefix(kind, displayName);
}
